import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { enabledToolKeys, isToolAvailable, toolKeyForCatalogKind } from '@/utils/vendor-kind';
import { isDecisionNode, isRulesNode, isTaskNode } from '../../store/types';
import type { PromptMentionKind, PromptMentionSource } from './prompt-mention-items';

/** Everything a prompt can legitimately reference, for chip-highlighting known references in prose. */
export type ReferenceIndex = {
  /** Input field names, addressed as `inputs.<name>`. */
  inputs: Set<string>;
  /** Task field names and rules keys, addressed as `outputs.<name>`. */
  outputs: Set<string>;
  /** Exact task names, matched verbatim. */
  tasks: string[];
  /** Display names of tools enabled on this agent, matched verbatim. */
  tools: string[];
  /** Names of this agent's skill folders, matched verbatim. */
  skills: string[];
};

export const EMPTY_REFERENCE_INDEX: ReferenceIndex = {
  inputs: new Set(),
  outputs: new Set(),
  tasks: [],
  tools: [],
  skills: [],
};

export type ReferenceMatch = {
  start: number;
  end: number;
  kind: PromptMentionKind;
};

// Verbatim name matching needs some length to avoid chipping everyday words
// (a task named "Run" would otherwise decorate every "Run" in prose).
const MIN_NAME_LENGTH = 3;

// The agent SDK discovers skills as folders, so a bare top-level file is not one to mention.
export const skillNames = (skills: SkillFileNode[]): string[] =>
  skills.flatMap(node => (node.kind === 'folder' && node.name.trim().length >= MIN_NAME_LENGTH ? [node.name] : []));

export const buildReferenceIndex = ({ bundle, toolCatalog }: PromptMentionSource): ReferenceIndex => {
  const outputs = bundle.nodes.flatMap(node => {
    if (isTaskNode(node)) return node.fields.map(f => f.name.trim());
    if (isRulesNode(node)) return [node.key.trim()];
    if (isDecisionNode(node)) return node.questions.map(question => question.key.trim());
    return [];
  });
  const enabledKeys = enabledToolKeys(bundle.tools);
  return {
    inputs: new Set(bundle.inputs.map(input => input.name.trim()).filter(Boolean)),
    outputs: new Set(outputs.filter(Boolean)),
    tasks: bundle.nodes
      .filter(isTaskNode)
      .map(node => node.name.trim())
      .filter(name => name.length >= MIN_NAME_LENGTH),
    tools: toolCatalog
      .filter(tool => isToolAvailable(tool) && enabledKeys.has(toolKeyForCatalogKind(tool.kind)))
      .map(tool => tool.displayName)
      .filter(name => name.length >= MIN_NAME_LENGTH),
    skills: skillNames(bundle.skills),
  };
};

const isWordChar = (char: string | undefined): boolean => !!char && /[A-Za-z0-9_]/.test(char);

/**
 * Finds known references in a run of plain text: `inputs.<name>` / `outputs.<name>` tokens
 * (only when the name actually exists on the graph) and exact task/tool names.
 * Returns non-overlapping matches in document order, preferring the longer match on overlap.
 */
export const findReferenceMatches = (text: string, refs: ReferenceIndex): ReferenceMatch[] => {
  const candidates: ReferenceMatch[] = [];

  const tokenRe = /(inputs|outputs)\.([A-Za-z_][A-Za-z0-9_]*)/g;
  for (const match of text.matchAll(tokenRe)) {
    if (isWordChar(text[match.index - 1])) continue;
    const [token, namespace, name] = match;
    const known = namespace === 'inputs' ? refs.inputs : refs.outputs;
    if (!known.has(name)) continue;
    candidates.push({
      start: match.index,
      end: match.index + token.length,
      kind: namespace === 'inputs' ? 'input' : 'output',
    });
  }

  const scanNames = (names: string[], kind: PromptMentionKind) => {
    for (const name of names) {
      let idx = text.indexOf(name);
      while (idx !== -1) {
        if (!isWordChar(text[idx - 1]) && !isWordChar(text[idx + name.length])) {
          candidates.push({ start: idx, end: idx + name.length, kind });
        }
        idx = text.indexOf(name, idx + name.length);
      }
    }
  };
  scanNames(refs.tasks, 'task');
  scanNames(refs.tools, 'tool');
  scanNames(refs.skills, 'skill');

  candidates.sort((a, b) => a.start - b.start || b.end - a.end);
  const matches: ReferenceMatch[] = [];
  let lastEnd = -1;
  for (const candidate of candidates) {
    if (candidate.start < lastEnd) continue;
    matches.push(candidate);
    lastEnd = candidate.end;
  }
  return matches;
};

export const findSkillMentions = (text: string, skills: string[]): Set<string> =>
  new Set(
    findReferenceMatches(text, { ...EMPTY_REFERENCE_INDEX, skills }).map(match => text.slice(match.start, match.end)),
  );
